/**
 * ResultsPage — Steward Admin
 *
 * TIME-BASED (Phase 1):
 *   All eligible drivers auto-populate.
 *   Enter times → positions auto-calculate → submit → leaderboard shows times.
 *
 * POSITION-BASED (Phase 2 & 3) — Multi-round:
 *   Step 1 — Select Participants: pick which drivers are racing.
 *   Step 2 — Enter results per round: Round 1, Round 2, etc.
 *            Each round awards 25/18/15/12/10/8/6/4 points independently.
 *            Points accumulate across rounds automatically.
 *            Submitting a round never touches other rounds' data.
 *   The leaderboard shows the combined total from all rounds.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  onEvents, onDrivers, onResults, updateEvent,
  submitResults, parseTimeToMs, formatMs, TITLES, POSITION_POINTS,
} from '../services/tripleCrown';
import useChampionship from '../hooks/useChampionship';

export default function ResultsPage() {
  const { champ } = useChampionship();
  const [events, setEvents] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [allResults, setAllResults] = useState([]);  // all results for this event (all rounds)
  const [entries, setEntries] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  // Round management
  const [activeRound, setActiveRound] = useState(1);

  // Participant selection
  const [selectingDrivers, setSelectingDrivers] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState(new Set());
  const [savingParticipants, setSavingParticipants] = useState(false);

  // Track drivers removed from the current round via ✕ button (ref to avoid useEffect loops)
  const removedDriverIdsRef = useRef(new Set());

  // Load events, drivers
  useEffect(() => {
    if (!champ) { setEvents([]); setDrivers([]); return; }
    const unsubs = [
      onEvents(champ.id, setEvents),
      onDrivers(champ.id, setDrivers),
    ];
    return () => unsubs.forEach(fn => fn());
  }, [champ]);

  // Load ALL results for the event (all rounds)
  useEffect(() => {
    if (!selectedEventId) { setAllResults([]); return; }
    /* One result per driver per round (owner, 3 Nov). A re-submit deletes
       and re-creates the round, and the live listener can briefly hold the
       old and new copies together — which doubled the rows until a reload.
       Keep the newest of each. */
    return onResults(selectedEventId, (rows) => {
      const ts = (r) => (r.createdAt && typeof r.createdAt.toMillis === 'function') ? r.createdAt.toMillis() : Number.MAX_SAFE_INTEGER;
      const best = new Map();
      rows.forEach((r) => {
        const k = `${r.driver}|${r.round || 1}`;
        const cur = best.get(k);
        if (!cur || ts(r) >= ts(cur)) best.set(k, r);
      });
      setAllResults(rows.filter(r => best.get(`${r.driver}|${r.round || 1}`) === r));
    });
  }, [selectedEventId]);

  // Reset round when switching events
  useEffect(() => {
    setActiveRound(1);
    setSelectingDrivers(false);
    removedDriverIdsRef.current = new Set();
  }, [selectedEventId]);

  const selectedEvent = events.find(e => e.id === selectedEventId);
  const isTimeBased = selectedEvent?.scoringMode === 'time';
  const showPoints = !isTimeBased && selectedEvent?.awardsPoints !== false;
  const hasParticipants = (selectedEvent?.participants || []).length > 0;

  // All active drivers for this title
  const eligibleDrivers = useMemo(() => {
    if (!selectedEvent) return [];
    return drivers.filter(d =>
      d.status === 'active' && (d.titles || []).includes(selectedEvent.titleId)
    );
  }, [drivers, selectedEvent]);

  // For position-based: only selected participants
  const raceDrivers = useMemo(() => {
    if (!selectedEvent) return [];
    if (isTimeBased) return eligibleDrivers;
    if (!hasParticipants) return [];
    const pSet = new Set(selectedEvent.participants);
    return drivers.filter(d => pSet.has(d.id));
  }, [drivers, selectedEvent, eligibleDrivers, isTimeBased, hasParticipants]);

  // ── Round logic for position-based events ──

  // Which rounds have submitted results?
  const submittedRounds = useMemo(() => {
    const rounds = new Set();
    allResults.forEach(r => rounds.add(r.round || 1));
    return Array.from(rounds).sort((a, b) => a - b);
  }, [allResults]);

  // Results for the active round
  const activeRoundResults = useMemo(() => {
    return allResults.filter(r => (r.round || 1) === activeRound);
  }, [allResults, activeRound]);

  const activeRoundHasResults = activeRoundResults.length > 0;

  // How many rounds total to show tabs for
  const totalRoundTabs = useMemo(() => {
    if (isTimeBased) return 1;
    const maxSubmitted = submittedRounds.length > 0 ? Math.max(...submittedRounds) : 0;
    // Show submitted rounds + one more for the next round
    return Math.max(maxSubmitted + 1, 1);
  }, [submittedRounds, isTimeBased]);

  // Accumulated points per driver across all rounds
  const accumulatedPoints = useMemo(() => {
    const map = {}; // driverId → { totalPoints, roundBreakdown: { round: points } }
    allResults.forEach(r => {
      const did = r.driver;
      if (!map[did]) map[did] = { totalPoints: 0, rounds: {} };
      const rd = r.round || 1;
      const pts = r.finalPoints || 0;
      map[did].totalPoints += pts;
      map[did].rounds[rd] = (map[did].rounds[rd] || 0) + pts;
    });
    return map;
  }, [allResults]);

  // Init participant selection
  useEffect(() => {
    if (selectedEvent && !isTimeBased) {
      setSelectedParticipants(new Set(selectedEvent.participants || []));
    }
  }, [selectedEvent, isTimeBased]);

  // Init entries for active round (filters out any drivers removed via ✕)
  useEffect(() => {
    if (!selectedEvent) return;
    const removed = removedDriverIdsRef.current;

    if (activeRoundHasResults) {
      // Pre-fill from existing results for this round
      const existingEntries = activeRoundResults
        .filter(r => !removed.has(r.driver))
        .map(r => {
          const driver = drivers.find(d => d.id === r.driver);
          return {
            driverId: r.driver,
            driverName: driver?.name || r.driver,
            position: r.position || null,
            sessionTime: r.sessionTime || '',
            fastestLapTime: '',
            fastestLap: (r.fastestLapBonus || 0) > 0,
          };
        });

      // For time-based events, also include eligible drivers who aren't in existing results yet
      if (isTimeBased) {
        const existingIds = new Set(activeRoundResults.map(r => r.driver));
        const newDriverEntries = eligibleDrivers
          .filter(d => !existingIds.has(d.id) && !removed.has(d.id))
          .map(d => ({
            driverId: d.id,
            driverName: d.name,
            position: null,
            sessionTime: '',
            fastestLapTime: '',
            fastestLap: false,
          }));
        setEntries([...existingEntries, ...newDriverEntries]);
      } else {
        setEntries(existingEntries);
      }
      return;
    }

    // New round — show blank entries for race drivers
    const driversToUse = isTimeBased ? eligibleDrivers : raceDrivers;
    setEntries(driversToUse
      .filter(d => !removed.has(d.id))
      .map((d, i) => ({
        driverId: d.id,
        driverName: d.name,
        position: isTimeBased ? null : '',
        sessionTime: '',
        fastestLapTime: '',
        fastestLap: false,
      })));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibleDrivers, raceDrivers, selectedEvent, isTimeBased, activeRound, activeRoundResults, activeRoundHasResults, drivers]);

  const updateEntry = useCallback((idx, field, value) => {
    setEntries(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      if (field === 'fastestLap' && value) {
        next.forEach((e, i) => { if (i !== idx) next[i] = { ...e, fastestLap: false }; });
      }
      return next;
    });
  }, []);

  // Auto-sort time-based entries
  const sortedEntries = useMemo(() => {
    if (!isTimeBased) return entries;
    const withTime = [];
    const withoutTime = [];
    entries.forEach(e => {
      const ms = parseTimeToMs(e.sessionTime);
      if (ms) withTime.push({ ...e, _ms: ms });
      else withoutTime.push({ ...e, _ms: null });
    });
    withTime.sort((a, b) => a._ms - b._ms);
    const sorted = [];
    withTime.forEach((e, i) => sorted.push({ ...e, position: i + 1 }));
    withoutTime.forEach(e => sorted.push({ ...e, position: null }));
    return sorted;
  }, [entries, isTimeBased]);

  // Toggle participant
  const toggleParticipant = (driverId) => {
    setSelectedParticipants(prev => {
      const next = new Set(prev);
      if (next.has(driverId)) next.delete(driverId); else next.add(driverId);
      return next;
    });
  };

  // Save participants
  const saveParticipants = async () => {
    if (!selectedEventId) return;
    setSavingParticipants(true);
    try {
      await updateEvent(selectedEventId, { participants: Array.from(selectedParticipants) });
      setSelectingDrivers(false);
      setToast({ type: 'success', msg: `${selectedParticipants.size} drivers selected for this race` });
    } catch (err) {
      setToast({ type: 'error', msg: err.message || 'Failed to save participants' });
    } finally {
      setSavingParticipants(false);
    }
  };

  // Submit results for active round
  const handleSubmit = async () => {
    if (!selectedEventId) return;
    const hasRemovals = removedDriverIdsRef.current.size > 0;

    // Allow submit with 0 entries if there are removals to process
    if (entries.length === 0 && !hasRemovals) return;

    // For time-based events, only submit entries that have a time entered
    // (not every eligible driver may have participated)
    let entriesToSubmit;
    if (isTimeBased) {
      entriesToSubmit = sortedEntries.filter(e => e.sessionTime && e.sessionTime.trim());
      if (entriesToSubmit.length === 0 && !hasRemovals) {
        setToast({ type: 'error', msg: 'Enter at least one session time to submit.' });
        return;
      }
    } else {
      entriesToSubmit = entries;
    }

    for (const e of entriesToSubmit) {
      if (isTimeBased) {
        if (!parseTimeToMs(e.sessionTime)) {
          setToast({ type: 'error', msg: `Invalid time format for ${e.driverName}. Use M:SS.mmm` });
          return;
        }
      } else {
        if (!e.position) {
          setToast({ type: 'error', msg: `Position required for ${e.driverName}` });
          return;
        }
      }
      if (e.sessionTime && !parseTimeToMs(e.sessionTime)) {
        setToast({ type: 'error', msg: `Invalid time format for ${e.driverName}. Use M:SS.mmm` });
        return;
      }
    }

    setSubmitting(true);
    try {
      const results = entriesToSubmit.map(e => ({
        driverId: e.driverId,
        position: e.position ? parseInt(e.position) : null,
        sessionTime: e.sessionTime || null,
        sessionTimeMs: parseTimeToMs(e.sessionTime),
        fastestLapMs: e.fastestLap && e.fastestLapTime ? parseTimeToMs(e.fastestLapTime) : null,
      }));
      // Pass the round number and removed drivers — Cloud Function replaces this round's data
      // and deletes results for any removed drivers
      const removedIds = Array.from(removedDriverIdsRef.current);
      await submitResults({ eventId: selectedEventId, results, round: activeRound, removedDriverIds: removedIds });
      removedDriverIdsRef.current = new Set();
      setToast({ type: 'success', msg: `Round ${activeRound} results submitted for ${entriesToSubmit.length} drivers!` });
    } catch (err) {
      setToast({ type: 'error', msg: err.message || 'Failed to submit results' });
    } finally {
      setSubmitting(false);
    }
  };

  // Auto-dismiss toast
  useEffect(() => {
    if (toast) { const t = setTimeout(() => setToast(null), 4000); return () => clearTimeout(t); }
  }, [toast]);

  // ── State for editing an already-submitted round ──
  const [editingRound, setEditingRound] = useState(false);

  // Reset editing when round changes
  useEffect(() => { setEditingRound(false); }, [activeRound]);

  const showEntryForm = !activeRoundHasResults || editingRound;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Enter Results</h1>
          <p className="page-subtitle">Select an event and record driver results</p>
        </div>
      </div>

      {/* Event Selector */}
      <div className="card" style={{ marginBottom: 18 }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Select Event</label>
          <select
            className="form-select"
            value={selectedEventId}
            onChange={e => setSelectedEventId(e.target.value)}
          >
            <option value="">Choose an event...</option>
            {events.map(evt => {
              const title = TITLES.find(t => t.id === evt.titleId);
              const pCount = (evt.participants || []).length;
              const roundInfo = evt.lastRoundSubmitted && evt.scoringMode === 'position' ? ` (R${evt.lastRoundSubmitted} done)` : '';
              const participantLabel = evt.scoringMode === 'position'
                ? (pCount > 0 ? ` [${pCount} drivers]` : ' [no lineup]')
                : '';
              return (
                <option key={evt.id} value={evt.id}>
                  {title?.short || evt.titleId} — {evt.phaseName || `Phase ${evt.phase}`}
                  {evt.track ? ` — ${evt.track}` : ''}{participantLabel}{roundInfo}
                </option>
              );
            })}
          </select>
        </div>
      </div>

      {/* Event info badges */}
      {selectedEvent && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
          <span className="badge badge-teal">{TITLES.find(t => t.id === selectedEvent.titleId)?.name}</span>
          <span className="badge badge-muted">{selectedEvent.phaseName || `Phase ${selectedEvent.phase}`}</span>
          <span className="badge badge-muted">Scoring: {selectedEvent.scoringMode}</span>
          {selectedEvent.track && <span className="badge badge-muted">{selectedEvent.track}</span>}
          <span className={`badge ${selectedEvent.status === 'completed' ? 'badge-success' : 'badge-gold'}`}>
            {selectedEvent.status}
          </span>
          {!isTimeBased && (
            <span className={`badge ${hasParticipants ? 'badge-teal' : 'badge-muted'}`}>
              {hasParticipants ? `${selectedEvent.participants.length} racing` : 'No lineup'}
            </span>
          )}
          {!isTimeBased && submittedRounds.length > 0 && (
            <span className="badge badge-success">
              {submittedRounds.length} round{submittedRounds.length !== 1 ? 's' : ''} submitted
            </span>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════
          POSITION-BASED: Participant Selection
          ══════════════════════════════════════ */}
      {selectedEvent && !isTimeBased && !allResults.length && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-header">
            <h3 className="card-title">
              {selectingDrivers ? 'Select Race Participants' : 'Race Lineup'}
            </h3>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {hasParticipants && !selectingDrivers && (
                <span className="badge badge-teal">{selectedEvent.participants.length} drivers</span>
              )}
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setSelectingDrivers(!selectingDrivers)}
              >
                {selectingDrivers ? 'Cancel' : hasParticipants ? 'Edit Lineup' : '+ Select Drivers'}
              </button>
            </div>
          </div>

          {selectingDrivers ? (
            <>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
                Tick the drivers racing in this event. This populates the leaderboard before the race.
              </p>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => setSelectedParticipants(new Set(eligibleDrivers.map(d => d.id)))}>Select All</button>
                <button className="btn btn-secondary btn-sm" onClick={() => setSelectedParticipants(new Set())}>Clear All</button>
              </div>
              <div className="table-wrap">
                <table>
                  <thead><tr><th style={{ width: 40 }}></th><th></th><th>Driver</th><th>Team</th></tr></thead>
                  <tbody>
                    {eligibleDrivers.map(d => (
                      <tr key={d.id} onClick={() => toggleParticipant(d.id)}
                        style={{ cursor: 'pointer', background: selectedParticipants.has(d.id) ? 'rgba(18,208,255,0.06)' : undefined }}>
                        <td><input type="checkbox" checked={selectedParticipants.has(d.id)} onChange={() => toggleParticipant(d.id)} /></td>
                        <td style={{ width: 40 }}>
                          {d.photoUrl
                            ? <img src={d.photoUrl} alt="" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover' }} />
                            : <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--bg-input)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>{d.name?.[0] || '?'}</div>}
                        </td>
                        <td style={{ fontWeight: 600 }}>{d.name}</td>
                        <td style={{ color: 'var(--text-muted)' }}>{d.team || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button className="btn btn-secondary" onClick={() => setSelectingDrivers(false)}>Cancel</button>
                <button className={`btn ${selectedParticipants.size === 0 ? 'btn-danger' : 'btn-primary'}`} onClick={saveParticipants} disabled={savingParticipants}>
                  {savingParticipants ? 'Saving...' : selectedParticipants.size === 0 ? 'Clear Lineup' : `Save Lineup (${selectedParticipants.size} drivers)`}
                </button>
              </div>
            </>
          ) : hasParticipants ? (
            <div className="table-wrap">
              <table>
                <thead><tr><th style={{ width: 40 }}>#</th><th></th><th>Driver</th><th>Team</th></tr></thead>
                <tbody>
                  {raceDrivers.map((d, i) => (
                    <tr key={d.id}>
                      <td style={{ color: 'var(--text-muted)' }}>{i + 1}</td>
                      <td style={{ width: 40 }}>
                        {d.photoUrl
                          ? <img src={d.photoUrl} alt="" style={{ width: 30, height: 30, borderRadius: '50%', objectFit: 'cover' }} />
                          : <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--bg-input)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13 }}>{d.name?.[0] || '?'}</div>}
                      </td>
                      <td style={{ fontWeight: 600 }}>{d.name}</td>
                      <td style={{ color: 'var(--text-muted)' }}>{d.team || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>No drivers selected yet. Click "Select Drivers" to pick the race lineup.</p>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════
          ROUND TABS (position-based only)
          ══════════════════════════════════════ */}
      {selectedEvent && !isTimeBased && hasParticipants && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 18, alignItems: 'center', flexWrap: 'wrap' }}>
          {Array.from({ length: totalRoundTabs }, (_, i) => i + 1).map(rd => {
            const hasData = submittedRounds.includes(rd);
            const isActive = rd === activeRound;
            return (
              <button
                key={rd}
                className={`btn btn-sm ${isActive ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setActiveRound(rd)}
                style={{ minWidth: 90, position: 'relative' }}
              >
                Round {rd}
                {hasData && !isActive && (
                  <span style={{ marginLeft: 6, fontSize: 10, opacity: 0.7 }}>✓</span>
                )}
              </button>
            );
          })}
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => {
              const nextRound = totalRoundTabs + 1;
              setActiveRound(nextRound);
            }}
            style={{ minWidth: 40 }}
            title="Add another round"
          >+</button>
        </div>
      )}

      {/* ══════════════════════════════════════
          SUBMITTED RESULTS for active round
          ══════════════════════════════════════ */}
      {activeRoundHasResults && !editingRound && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-header">
            <h3 className="card-title">
              {isTimeBased ? 'Submitted Results' : `Round ${activeRound} Results`}
            </h3>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span className="badge badge-success">{activeRoundResults.length} entries</span>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditingRound(true)}>
                Edit Round
              </button>
            </div>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Pos</th>
                  <th>Driver</th>
                  <th>Time</th>
                  {showPoints && <th>Base Pts</th>}
                  {showPoints && <th>Penalty</th>}
                  {showPoints && <th>Round Pts</th>}
                </tr>
              </thead>
              <tbody>
                {activeRoundResults.map(r => {
                  const driver = drivers.find(d => d.id === r.driver);
                  return (
                    <tr key={r.id}>
                      <td style={{ fontWeight: 700 }}>{r.position || '—'}</td>
                      <td>{driver?.name || r.driver}</td>
                      <td style={{ fontFamily: 'monospace' }}>
                        {r.sessionTime || (r.sessionTimeMs ? formatMs(r.sessionTimeMs) : '—')}
                      </td>
                      {showPoints && <td>{r.basePoints}</td>}
                      {showPoints && (
                        <td style={{ color: r.penaltyPoints > 0 ? 'var(--danger)' : undefined }}>
                          {r.penaltyPoints > 0 ? `-${r.penaltyPoints}` : '0'}
                        </td>
                      )}
                      {showPoints && (
                        <td style={{ fontWeight: 700, color: 'var(--teal)' }}>{r.finalPoints}</td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════
          ENTRY FORM for active round
          ══════════════════════════════════════ */}
      {selectedEvent && showEntryForm && (isTimeBased || hasParticipants) && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-header">
            <h3 className="card-title">
              {isTimeBased
                ? 'Enter Session Times'
                : editingRound
                  ? `Edit Round ${activeRound}`
                  : `Enter Round ${activeRound} Positions`}
            </h3>
            <span className="badge badge-teal">{entries.length} drivers</span>
          </div>

          {entries.length === 0 ? (
            <div>
              <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                {removedDriverIdsRef.current.size > 0
                  ? `All drivers removed. Submit to delete ${removedDriverIdsRef.current.size} result(s) from Firestore.`
                  : isTimeBased ? 'No eligible drivers for this title' : 'Select drivers in the lineup above first'}
              </p>
              {removedDriverIdsRef.current.size > 0 && (
                <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                  <button className="btn btn-danger" onClick={handleSubmit} disabled={submitting} style={{ padding: '10px 24px' }}>
                    {submitting ? 'Deleting...' : `Delete ${removedDriverIdsRef.current.size} Result(s)`}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              {isTimeBased ? (
                /* ── Time-based entry ── */
                <>
                  <div className="table-wrap">
                    <table>
                      <thead><tr><th>Driver</th><th>Session Time *</th><th style={{ width: 50 }}></th></tr></thead>
                      <tbody>
                        {entries.map((entry, idx) => (
                          <tr key={entry.driverId}>
                            <td style={{ fontWeight: 600 }}>{entry.driverName}</td>
                            <td>
                              <input className="form-input" value={entry.sessionTime}
                                onChange={e => updateEntry(idx, 'sessionTime', e.target.value)}
                                placeholder="M:SS.mmm"
                                style={{ width: 160, fontFamily: 'monospace', padding: '6px 10px',
                                  borderColor: entry.sessionTime && !parseTimeToMs(entry.sessionTime) ? 'var(--danger)' : undefined }} />
                            </td>
                            <td>
                              <button className="btn btn-danger btn-sm" style={{ padding: '4px 8px', fontSize: 11 }}
                                onClick={() => {
                                  removedDriverIdsRef.current.add(entry.driverId);
                                  setEntries(prev => prev.filter(e => e.driverId !== entry.driverId));
                                }}>
                                ✕
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {sortedEntries.some(e => e.position) && (
                    <div style={{ marginTop: 18 }}>
                      <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 10, color: 'var(--teal)' }}>Auto-Ranked Standings</h4>
                      <div className="table-wrap">
                        <table>
                          <thead><tr><th style={{ width: 50 }}>Pos</th><th>Driver</th><th>Time</th><th>Gap</th></tr></thead>
                          <tbody>
                            {sortedEntries.filter(e => e.position).map((entry, idx) => {
                              const leaderMs = sortedEntries[0]?._ms || 0;
                              const gapMs = (entry._ms || 0) - leaderMs;
                              return (
                                <tr key={entry.driverId}>
                                  <td style={{ fontWeight: 700, color: entry.position <= 3 ? 'var(--gold, #ffc857)' : undefined }}>P{entry.position}</td>
                                  <td style={{ fontWeight: 600 }}>{entry.driverName}</td>
                                  <td style={{ fontFamily: 'monospace' }}>{entry.sessionTime}</td>
                                  <td style={{ fontFamily: 'monospace', color: 'var(--text-muted)' }}>{idx === 0 ? '—' : `+${formatMs(gapMs)}`}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
                    Phase 1 is time-based. Positions auto-calculate from session times (fastest = P1). No points are awarded — top drivers advance.
                  </p>
                </>
              ) : (
                /* ── Position-based entry for this round ── */
                <>
                  <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
                    Enter finishing positions for Round {activeRound}.
                    {showPoints
                      ? <> Points are calculated automatically (25/18/15/12/10/8/6/4).{submittedRounds.length > 0 && ` Points from other rounds are preserved.`}</>
                      : ' No points awarded for this round — position only.'}
                  </p>
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th style={{ width: 70 }}>Position</th>
                          <th>Driver</th>
                          <th>Session Time</th>
                          <th style={{ width: 130 }}>Fastest Lap</th>
                          {showPoints && <th style={{ width: 80 }}>Rd Pts</th>}
                          <th style={{ width: 50 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {entries.map((entry, idx) => {
                          const pts = entry.position && entry.position <= POSITION_POINTS.length
                            ? POSITION_POINTS[entry.position - 1] + (entry.fastestLap ? 1 : 0)
                            : '—';
                          return (
                            <tr key={entry.driverId}>
                              <td>
                                <input className="form-input" type="number" min="1" max={entries.length}
                                  value={entry.position || ''}
                                  onChange={e => updateEntry(idx, 'position', e.target.value)}
                                  style={{ width: 60, textAlign: 'center', padding: '6px 8px' }} />
                              </td>
                              <td style={{ fontWeight: 600 }}>{entry.driverName}</td>
                              <td>
                                <input className="form-input" value={entry.sessionTime}
                                  onChange={e => updateEntry(idx, 'sessionTime', e.target.value)}
                                  placeholder="M:SS.mmm"
                                  style={{ width: 150, fontFamily: 'monospace', padding: '6px 10px' }} />
                              </td>
                              <td>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 12 }}>
                                  <input type="checkbox" checked={entry.fastestLap || false}
                                    onChange={e => updateEntry(idx, 'fastestLap', e.target.checked)} />
                                  FL (+1)
                                </label>
                              </td>
                              {showPoints && <td style={{ fontWeight: 700, color: 'var(--teal)', fontSize: 16 }}>{pts}</td>}
                              <td>
                                <button className="btn btn-danger btn-sm" style={{ padding: '4px 8px', fontSize: 11 }}
                                  onClick={() => {
                                    removedDriverIdsRef.current.add(entry.driverId);
                                    setEntries(prev => prev.filter(e => e.driverId !== entry.driverId));
                                  }}>
                                  ✕
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                {editingRound && (
                  <button className="btn btn-secondary" onClick={() => setEditingRound(false)}>Cancel</button>
                )}
                <button className="btn btn-primary" onClick={handleSubmit} disabled={submitting} style={{ padding: '10px 24px' }}>
                  {submitting
                    ? 'Submitting...'
                    : isTimeBased
                      ? `Submit ${sortedEntries.filter(e => e.sessionTime && e.sessionTime.trim()).length} Results`
                      : `Submit Round ${activeRound} (${entries.length} drivers)`}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════
          ACCUMULATED TOTALS (position-based, multi-round)
          ══════════════════════════════════════ */}
      {selectedEvent && !isTimeBased && submittedRounds.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Accumulated Standings</h3>
            <span className="badge badge-teal">{submittedRounds.length} round{submittedRounds.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 50 }}>Pos</th>
                  <th>Driver</th>
                  {submittedRounds.map(rd => (
                    <th key={rd} style={{ textAlign: 'center', fontSize: 12 }}>R{rd}</th>
                  ))}
                  <th style={{ fontWeight: 700 }}>Total</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(accumulatedPoints)
                  .sort(([, a], [, b]) => b.totalPoints - a.totalPoints)
                  .map(([driverId, data], idx) => {
                    const driver = drivers.find(d => d.id === driverId);
                    return (
                      <tr key={driverId}>
                        <td style={{ fontWeight: 700, color: idx < 3 ? 'var(--gold, #ffc857)' : undefined }}>
                          {idx + 1}
                        </td>
                        <td style={{ fontWeight: 600 }}>{driver?.name || driverId}</td>
                        {submittedRounds.map(rd => (
                          <td key={rd} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                            {data.rounds[rd] || 0}
                          </td>
                        ))}
                        <td style={{ fontWeight: 700, color: 'var(--teal)', fontSize: 16 }}>
                          {data.totalPoints}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 10 }}>
            Points accumulate across all rounds. The leaderboard reflects these totals.
          </p>
        </div>
      )}

      {/* Toast */}
      {toast && <div className={`toast toast-${toast.type}`}>{toast.msg}</div>}
    </div>
  );
}
